import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import { Modal } from "@wwg/ui";
import { Select } from "shared/Select/Select";
import Button from "shared/Button";
import Input from "shared/@forms/Input";
import ModalActionBar from "shared/ModalActionBar";
import emailTemplate from "../../Shared/EmailTemplate/emailTemplate";

import { useUsersList } from "shared/@hooks/useUsersList";

import styles from "./EditIncidentInfoModal.module.css";

import {
  PDFDownloadLink,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
} from "@react-pdf/renderer";

/**Modal that opens when view is edit case info is clicked in the actions dropdown on an incident details page.  */
export function EditIncidentInfoModal({
  onSave,
  onSend,
  isOpen,
  onClose,
  editInfo,
}) {
  //Top row of selects
  const [status, setStatus] = useState("Select");
  const [type, setType] = useState("Select");
  const [caseOwner, setCaseOwner] = useState("Select");
  const [severity, setSeverity] = useState("Select");
  const [createdAt, setCreatedAt] = useState(null);
  const [template, setTemplate] = useState("Choose Template");
  const [incidentNumber, setIncidentNumber] = useState(null);
  const [sendToSelect, setSendToSelect] = useState("Send To");
  const [caseOwnerList, setCaseOwnerList] = useState([]);

  const list = useUsersList();

  // get list of caseOwners from all WWDB emails
  useEffect(() => {
    if (list) {
      const filteredEmails = list
        .filter((item) => item.mail && item.mail.includes("@wwdb.com"))
        .map((item) => item.displayName);

      setCaseOwnerList(filteredEmails);
    }
  }, [list]);

  // populate with preexisting data if available
  const [_editInfo, setEditInfo] = useState({
    status: null,
    type: null,
    owner: null,
    severity: null,
    createdAt: null,
    incidentNumber: null,
  });
  useEffect(() => {
    editInfo &&
      setEditInfo({
        status: editInfo.case.status,
        type: editInfo.case.problemType,
        owner: editInfo.case.owner,
        severity: editInfo.case.severity,
        createdAt: editInfo.case.createdAt,
        incidentNumber: editInfo.case.incidentNumber,
      });
  }, [editInfo]);
  useEffect(() => {
    if (_editInfo) {
      _editInfo.status && setStatus(_editInfo.status);
      _editInfo.type && setType(_editInfo.type);
      _editInfo.owner && setCaseOwner(_editInfo.owner);
      _editInfo.severity && setSeverity(_editInfo.severity);
      _editInfo.createdAt && setCreatedAt(_editInfo.createdAt);
      _editInfo.incidentNumber && setIncidentNumber(_editInfo.incidentNumber);
    }
  }, [_editInfo]);

  const [errors, setErrors] = useState({
    status: "",
    type: "",
    caseOwner: "",
    severity: "",
  });

  const handleSubmit = () => {
    //Check for errors and then pass the information
    let errors = {
      status: "",
      type: "",
      caseOwner: "",
      severity: "",
    };
    if (status === "Select") errors.status = "Please select an option";

    if (type === "Select") errors.type = "Please select an option";

    if (caseOwner === "Select") errors.caseOwner = "Please select an option";

    if (severity === "Select") errors.severity = "Please select an option";

    //Check that theres no errors
    let empty = true;
    for (let key in errors) {
      if (errors[key].length) empty = false;
    }
    if (empty) {
      onSave({
        status: status,
        type: type,
        caseOwner: caseOwner,
        severity: severity,
      });
      onClose();
    } else {
      setErrors({ ...errors });
    }
  };

  //Email
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const [emailErrors, setEmailErrors] = useState({
    subject: "",
    body: "",
  });

  const handleEmail = () => {
    //Check for errors and then pass the information
    const email = {
      subject: subject,
      body: body,
    };
    const errors = {
      subject: "",
      body: "",
    };

    if (!email.subject.length) errors.subject = "Please enter a subject";

    if (!email.body.length) errors.body = "Please enter a body";

    if (sendToSelect === "Send To") errors.sendTo = "Please select a recipient";

    //Check there's no errors
    let empty = true;
    for (let key in errors) {
      if (errors[key].length) empty = false;
    }

    if (empty) {
      onSave({
        status: status,
        type: type,
        caseOwner: caseOwner,
        severity: severity,
      });
      onSend({
        sendTo: sendToSelect,
        template: template,
        subject: email.subject,
        emailBody: email.body,
      });
      onClose();
    } else setEmailErrors(errors);
  };

  //useEffect to handle email templates
  useEffect(() => {
    if (template !== "Choose Template") {
      const getTemplate = emailTemplate(template);
      setSubject(getTemplate.subject);
      setBody(getTemplate.body);
    }
  }, [template]);

  // pdf stuff
  const pdfStyles = StyleSheet.create({
    page: {
      display: "flex",
      flexDirection: "row",
    },
    section: {
      marginBottom: "20px",
      padding: "20px",
      display: "flex",
      flexDirection: "column",
    },
    title: {
      textDecoration: "underline",
      marginBottom: "20px",
      marginTop: "20px",
      fontWeight: 100,
    },
    text: {
      whiteSpace: "pre-line",
      lineBreak: "anywhere",
    },
    row: {
      marginBottom: "10px",
    },
    listText: {
      whiteSpace: "pre-line",
      lineBreak: "anywhere",
      marginBottom: "20px",
    },
    listItem: {
      marginBottom: "10px",
    },
  });

  const formattedDateTime = new Date().toLocaleString("en-US", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const PDF = () => (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.title}>INCIDENT INFORMATION</Text>
          <Text style={pdfStyles.text}>Status: {status}</Text>
          <Text style={pdfStyles.text}>Problem Type: {type}</Text>
          <Text style={pdfStyles.text}>Case Owner: {caseOwner}</Text>
          <Text style={pdfStyles.text}>Severity: {severity}</Text>
          <Text style={pdfStyles.text}>Incident Filed At: {createdAt}</Text>
          {subject.length > 0 && body.length > 0 && sendToSelect !== "Send To" && (
            <>
              <Text style={pdfStyles.title}>EMAIL</Text>
              <Text style={pdfStyles.text}>Sent: {formattedDateTime}</Text>
              <Text style={pdfStyles.text}>To: {sendToSelect}</Text>
              <Text style={pdfStyles.text}>Subject: {subject}</Text>
              <Text style={pdfStyles.text}>Body: {body}</Text>
            </>
          )}
        </View>
      </Page>
    </Document>
  );

  return (
    <Modal
      visible={isOpen}
      onClick={onClose}
      children={
        <div className={styles.modalBody}>
          <div className={styles.title}> Edit Information </div>
          <div className={styles.hr}></div>
          <div className={styles.header}>
            <span className={styles.statusContainer}>
              <span className={styles.label}>Status</span>
              <span className={styles.select}>
                <Select
                  placeholder={status}
                  options={[
                    "New",
                    "Read",
                    "Reviewed",
                    "Action Required",
                    "Closed",
                  ]}
                  onChange={(i) => {
                    setStatus(i);
                    setErrors({ ...errors, status: "" });
                  }}
                />
              </span>
            </span>
            <span className={styles.statusContainer}>
              <span className={styles.label}>Type</span>
              <span className={styles.select}>
                <Select
                  placeholder={type}
                  options={[
                    "Harassment",
                    "Hate Speech",
                    "Violence",
                    "Lookup Abuse",
                    "Other",
                  ]}
                  onChange={(i) => {
                    setType(i);
                    setErrors({ ...errors, type: "" });
                  }}
                />
              </span>
            </span>
            <span className={styles.statusContainer}>
              <span className={styles.label}>Case Owner</span>
              <span className={styles.select}>
                <Select
                  placeholder={caseOwner}
                  options={caseOwnerList}
                  onChange={(i) => {
                    setCaseOwner(i);
                    setErrors({ ...errors, caseOwner: "" });
                  }}
                  isScrollable={true}
                />
              </span>
            </span>
            <span className={styles.statusContainer}>
              <span className={styles.label}>Severity</span>
              <span className={styles.select}>
                <Select
                  placeholder={severity}
                  options={[
                    "Level 1",
                    "Level 2",
                    "Level 3",
                    "Level 4",
                    "Level 5",
                  ]}
                  onChange={(i) => {
                    setSeverity(i);
                    setErrors({ ...errors, severity: "" });
                  }}
                />
              </span>
            </span>
            <span style={{ marginTop: "1.5em" }}>
              <Button className={styles.saveButton} onClick={handleSubmit}>
                Save
              </Button>
            </span>
          </div>
          <div className={styles.errorHeader}>
            <span>{errors.status}</span>
            <span>{errors.type}</span>
            <span>{errors.caseOwner}</span>
            <span>{errors.severity}</span>
          </div>
          <div className={styles.emailContainer}>
            <span className={styles.label}>Send an Email</span>
            <div className={styles.templateSelect}>
              <Select
                placeholder={sendToSelect}
                options={[
                  `Primary Contact - ${editInfo.victim.email}`,
                  `Reported Contact - ${editInfo.aggressor.email}`,
                  "System Administrator - support@wwdb.com",
                ]}
                onChange={setSendToSelect}
              />
            </div>
            <span className={styles.selectError}>{emailErrors.sendTo}</span>
            <span className={styles.templateSelect}>
              <Select
                placeholder={template}
                options={[
                  "Caution Letter",
                  "Review Letter",
                  "Probation Letter",
                  "Disciplinary Letter",
                  "Disciplinary Letter - Physical Threat",
                  "Withdrawl Letter",
                  "Admonish Letter",
                  "Reinstated Letter",
                  "Case Completion Letter",
                ]}
                onChange={(value) => {
                  setEmailErrors({ subject: "", body: "" });
                  setTemplate(value);
                }}
              />
            </span>

            <span>
              <Input
                className={styles.input}
                id="subject"
                placeholder="Subject Line"
                defaultValue={subject}
                //value={email.subject || ""}
                onChange={(e) => {
                  setEmailErrors({ ...emailErrors, subject: "" });
                  setSubject(e.target.value);
                }}
              />
            </span>
            <span className={styles.error}>{emailErrors.subject}</span>
            <span>
              <textarea
                className={styles.textArea}
                placeholder="Add text here..."
                id="body"
                defaultValue={body}
                //value={email.body || ""}
                onChange={(e) => {
                  setEmailErrors({ ...emailErrors, body: "" });
                  setBody(e.target.value);
                }}
              />
            </span>
            <span className={styles.error}>{emailErrors.body}</span>
          </div>
          <div className={styles.actionBar}>
            <ModalActionBar>
              <PDFDownloadLink
                document={<PDF />}
                fileName={`incident_${incidentNumber}`}
              >
                <Button className={styles.printPDFButton}>Print PDF</Button>
              </PDFDownloadLink>

              <Button className={styles.sendButton} onClick={handleEmail}>
                Send
              </Button>
            </ModalActionBar>
          </div>
        </div>
      }
    />
  );
}

EditIncidentInfoModal.propTypes = {
  /** Function called when the send btn is clicked.  */
  onSend: PropTypes.func,
  /** Getter to determine if modal is displayed  */
  isOpen: PropTypes.bool,
  /** Callback function for when the modal is closed  */
  onClose: PropTypes.func,
};
